import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import axios from "axios";
import { useEffect } from "react";

import { type DishWorkflowSchemaType } from "@fitness-app/ai";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type AiAgentRequest } from "@fitness-app/data-models/entities/AiAgentRequest";
import { getErrorMessage } from "@fitness-app/utils";
import {
  type NutrientsTargets,
  type TraineeNutritionGeneratorFormModel,
} from "@fitness-app/utils/src/nutrition/mealsPlanGenerators";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { usePermission } from "~/hooks/usePermission";
import { analyticsService, backgroundTasksApi, supabase } from "~/store/initializeStore";

type BackgroundTaskTiming = {
  totalMs: number;
  setupMs?: number;
  workflowMs?: number;
  steps: {
    stepId: string;
    status: string;
    startedAtMs: number;
    finishedAtMs?: number;
    durationMs?: number;
  }[];
};

export type BackgroundTaskResult = {
  data?: {
    status: string;
    result?: DishWorkflowSchemaType;
    fileName?: string;
    recordedAt?: string | null;
    durationSeconds?: number | null;
    meetingId?: string;
    analysis?: {
      score?: number;
      summary?: string;
    };
    error?: string;
  };
  time?: number;
  timings?: BackgroundTaskTiming;
  completedAt?: string;
  error?: string;
  failedAt?: string;
};

export type BackgroundTask = {
  id: string;
  type: string | null;
  status: string;
  data: {
    dishId?: string;
    dishTitle?: string;
    trainerId?: string;
    reason?: string;
    teamMemberId?: string;
    fileName?: string;
    bucket?: string;
    path?: string;
    recordedAt?: string | null;
  };
  requestId: string | null;
  result: BackgroundTaskResult | null;
  metadata: {
    taskType?: string;
    createdAt?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
};

type CreateDishIssueTasksPayload = {
  issues: {
    dishId: string;
    issueId: string;
    reason?: string;
    dishTitle: string;
  }[];
};

type CreateDishIssueTasksResponse = {
  run: {
    id: string;
  };
  accessToken: string;
  dishId: string;
  tags: string[];
};

type CreateNutritionPlanTaskPayload = Omit<TraineeNutritionGeneratorFormModel, "targets"> & {
  targets: NutrientsTargets;
  traineeId: string;
};

type CreateNutritionPlanTaskResponse = {
  run?: {
    id: string;
  };
  accessToken?: string;
  requestId: string;
  planId?: string;
  emptyTemplate?: boolean;
  tags: string[];
};

type CreateMealsPlanDishImagesPayload = {
  planId: string;
  /** `template` = szablon w bibliotece diet, `client` = dieta przypisana klientce. */
  planType: "template" | "client";
  overwrite?: boolean;
  prompt?: string;
  /** Policzona w panelu liczba potraw bez zdjęcia — trafia do `taskCount`, żeby lista miała co pokazać. */
  expectedDishes?: number;
};

type CreateMealsPlanDishImagesResponse = {
  run: {
    id: string;
  };
  accessToken: string;
  requestId: string;
  tags: string[];
};

type CancelAgentRequestPayload = {
  requestId: string;
};

type CancelAgentRequestResponse = {
  status: "success";
  requestId: string;
  canceledAt: string;
};

export const useBackgroundTasksQuery = ({
  enabled = false,
  excludeCompleted = true,
}: {
  enabled?: boolean;
  excludeCompleted?: boolean;
}) => {
  const { memberId } = useUserClaims();
  // Zadania AI całego konta ogląda ten, kto ma agentów w zakresie tenanta.
  const seesAllBackgroundTasks = usePermission(PermissionKey.AGENTS_USE, PermissionScope.ALL);
  const visibleMemberId = seesAllBackgroundTasks ? null : memberId;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const channel = supabase
      .channel(`ai-agent-request-changes-${visibleMemberId ?? "all"}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "ai_agent_request",
          ...(visibleMemberId ? { filter: `trainerId=eq.${visibleMemberId}` } : {}),
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: ["ai_agent_request"] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, queryClient, visibleMemberId]);

  const { data, isLoading } = useQuery(["ai_agent_request", visibleMemberId, excludeCompleted], {
    enabled,
    queryFn: async () => {
      let query = supabase
        .from("ai_agent_request")
        .select("*")
        .not("status", "eq", "DELETED")
        // Automation-generated requests (Low-ticket 2.0) are background work — not
        // part of the trainer's evaluation queue.
        .or("metadata->>source.is.null,metadata->>source.neq.program_automation");

      if (excludeCompleted) {
        query = query.not("status", "eq", "COMPLETED");
      }

      if (visibleMemberId) {
        query.eq("trainerId", visibleMemberId);
      }

      const { data } = await query
        .order("createdAt", { ascending: false })
        .limit(30)
        .throwOnError()
        .overrideTypes<AiAgentRequest[]>();

      return data;
    },
  });

  const createDishIssueTasksMutation = useMutation({
    mutationFn: async (payload: CreateDishIssueTasksPayload): Promise<CreateDishIssueTasksResponse> => {
      const response = await backgroundTasksApi.post<CreateDishIssueTasksResponse>(
        "/api/trainers/ai/create-dish-issue-tasks",
        { ...payload, trainerId: memberId },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["ai_agent_request"] });
    },
    onError: (error) => {
      message.error(`Błąd podczas tworzenia zadań: ${getErrorMessage(error)}`);
    },
  });

  const createNutritionPlanTaskMutation = useMutation({
    mutationFn: async (payload: CreateNutritionPlanTaskPayload): Promise<CreateNutritionPlanTaskResponse> => {
      const response = await backgroundTasksApi.post<CreateNutritionPlanTaskResponse>(
        "/api/trainers/ai/generate-nutrition-plan",
        { ...payload, trainerId: memberId },
      );
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.refetchQueries({ queryKey: ["ai_agent_request"] });
      message.success(
        response.emptyTemplate ? "Pusty szablon diety został utworzony." : "Generator diety został uruchomiony w tle.",
      );
    },
    onError: (error) => {
      message.error(`Błąd podczas uruchamiania generatora diety: ${getErrorMessage(error)}`);
    },
  });

  const createMealsPlanDishImagesMutation = useMutation({
    mutationFn: async (payload: CreateMealsPlanDishImagesPayload): Promise<CreateMealsPlanDishImagesResponse> => {
      const response = await backgroundTasksApi.post<CreateMealsPlanDishImagesResponse>(
        "/api/trainers/ai/generate-meals-plan-dish-images",
        { ...payload, trainerId: memberId },
      );
      return response.data;
    },
    onSuccess: (_, payload) => {
      queryClient.refetchQueries({ queryKey: ["ai_agent_request"] });
      message.success("Generowanie zdjęć potraw ruszyło w tle.");
      analyticsService.track("meals_plan_dish_images_generated", {
        planId: payload.planId,
        planType: payload.planType,
        overwrite: payload.overwrite,
        prompt: payload.prompt,
        expectedDishes: payload.expectedDishes,
      });
    },
    onError: (error) => {
      // 409 = generacja dla tego planu już leci. Serwer tłumaczy to po ludzku, więc pokazujemy
      // jego komunikat zamiast axiosowego "Request failed with status code 409".
      const serverMessage = axios.isAxiosError<{ error?: string }>(error) ? error.response?.data?.error : null;
      message.error(serverMessage || `Błąd podczas uruchamiania generatora zdjęć: ${getErrorMessage(error)}`);
    },
  });

  const cancelAgentRequestMutation = useMutation({
    mutationFn: async (payload: CancelAgentRequestPayload): Promise<CancelAgentRequestResponse> => {
      const response = await backgroundTasksApi.post<CancelAgentRequestResponse>(
        "/api/trainers/ai/cancel-agent-request",
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["ai_agent_request"] });
      message.success("Zadanie zostało anulowane.");
    },
    onError: (error) => {
      message.error(`Błąd podczas anulowania zadania: ${getErrorMessage(error)}`);
    },
  });

  return {
    createDishIssueTasks: createDishIssueTasksMutation.mutateAsync,
    isCreatingDishIssueTasks: createDishIssueTasksMutation.isLoading,
    createDishIssueTasksMutation,
    createNutritionPlanTask: createNutritionPlanTaskMutation.mutateAsync,
    isCreatingNutritionPlanTask: createNutritionPlanTaskMutation.isLoading,
    createNutritionPlanTaskMutation,
    createMealsPlanDishImages: createMealsPlanDishImagesMutation.mutateAsync,
    isCreatingMealsPlanDishImages: createMealsPlanDishImagesMutation.isLoading,
    createMealsPlanDishImagesMutation,
    cancelAgentRequest: cancelAgentRequestMutation.mutateAsync,
    cancelAgentRequestMutation,
    data,
    isLoading,
  };
};

export const useBackgroundTaskFallback = ({ requestId, enabled = false }: { requestId: string; enabled?: boolean }) => {
  const { data, isLoading } = useQuery(["background_task", requestId], {
    enabled,
    queryFn: async () => {
      const { data } = await supabase
        .from("background_task")
        .select("*")
        .eq("requestId", requestId)
        .throwOnError()
        .overrideTypes<BackgroundTask[]>();

      return data;
    },
  });

  return {
    fallbackTasks: data,
    isLoadingFallback: isLoading,
  };
};
